/**
 * /partner/network — channel-partner ("Stephanie mode") dashboard.
 *
 * Shows every Investor and Company the requesting user has been
 * attributed as the referrer for. v3 capability: single-hop, single-
 * owner attribution. Multi-hop network-graph traversal is v4.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Network, Building2, UserCircle2, ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';
import DashboardLayout from '../components/DashboardLayout';
import selfServeService, {
  PartnerNetworkResponse,
} from '../services/selfServeService';


const completionColor = (pct: number | null): string => {
  if (pct === null || pct === undefined) return 'text-gray-400';
  if (pct >= 60) return 'text-emerald-600';
  if (pct >= 40) return 'text-amber-600';
  return 'text-gray-500';
};


const NetworkPartnerDashboardPage: React.FC = () => {
  const [data, setData] = useState<PartnerNetworkResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const res = await selfServeService.getMyPartnerNetwork();
      if (res.error) {
        toast.error(res.error);
      } else if (res.data) {
        setData(res.data);
      }
      setLoading(false);
    })();
  }, []);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20 text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" />
          Loading network…
        </div>
      </DashboardLayout>
    );
  }

  const records = data?.records || [];
  const investorCount = data?.investor_count || 0;
  const companyCount = data?.company_count || 0;

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        <div>
          <h1 className="text-3xl font-semibold flex items-center gap-2">
            <Network className="w-7 h-7 text-indigo-500" />
            Your network
          </h1>
          <p className="text-gray-600 mt-1">
            Investors and companies you've introduced to the platform.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <div className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
              Investors
            </div>
            <div className="text-3xl font-semibold mt-1">{investorCount}</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <div className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
              Companies
            </div>
            <div className="text-3xl font-semibold mt-1">{companyCount}</div>
          </div>
        </div>

        {records.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-8 text-center text-gray-500">
            You haven't been attributed as the referrer for any records yet.
            When an admin tags an investor or company as introduced by you,
            it will appear here.
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-200">
            {records.map((r) => (
              <Link
                key={`${r.kind}-${r.id}`}
                to={r.kind === 'investor' ? `/investors/${r.id}` : `/companies/${r.id}`}
                className="flex items-center gap-4 p-4 hover:bg-gray-50"
              >
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
                  {r.kind === 'investor' ? (
                    <UserCircle2 className="w-5 h-5" />
                  ) : (
                    <Building2 className="w-5 h-5" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{r.name || '(unnamed)'}</div>
                  <div className="text-sm text-gray-500 truncate">{r.secondary || r.kind}</div>
                </div>
                <div className={`text-xs font-medium ${completionColor(r.research_completion_pct)}`}>
                  {r.research_completion_pct !== null
                    ? `${r.research_completion_pct}% complete`
                    : '—'}
                </div>
                <ArrowRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
              </Link>
            ))}
          </div>
        )}

        <div className="text-xs text-gray-500 mt-6">
          v3 capability: shows records explicitly attributed to you as the
          referrer. Multi-hop network-graph traversal (records introduced
          by people in your network) and per-partner data isolation are
          v4 features.
        </div>
      </div>
    </DashboardLayout>
  );
};

export default NetworkPartnerDashboardPage;
